import { useMemo } from 'react'
import router, { useRouter } from 'next/router'

import appConfig from '~/config/app'
import { getSubScriptionFromUser } from '~/familyAccount/utils/common.utils'
import useCurrentUser from '~/hooks/useCurrentUser'
import { useGetMeQuery } from '~/redux/apis/user-api'
import { MobileAppSubscriptionStatus, IUser } from '~/types/user-info-types'
import { StripeSubscriptionStatus } from '~/types/stripe-subscription'
import { isAuthenticated } from '~/auth/slices/auth.slice'

import { useAppSelector } from '../hooks'

export function isSubscriptionRequiredForUser(
  user?: IUser | null,
  urlPath?: string,
  considerPermissions?: boolean,
): boolean {
  // The school platform has no commerce — every seat is fully unlocked. Gating here rather than at
  // call sites covers `useHasPremiumAccess`, the `isSubscriptionRequiredOnUser` selector, and
  // through them the paywall modal and the encyclopedia click interceptor.
  if (appConfig.isSchoolPlatform) return false

  return Boolean(
    user?.isSubscriptionApplicable &&
      !userHasActiveSubscriptionWithAccessPermissions(user, urlPath, considerPermissions),
  )
}

function hasMobileAppSubscription(user?: IUser | null): boolean {
  if (!user) return false

  const status = user.validMobileAppSubscription?.subscriptionStatus

  return status === MobileAppSubscriptionStatus.TRIALING || status === MobileAppSubscriptionStatus.ACTIVE
}

export function userHasActiveSubscriptionWithAccessPermissions(
  user?: IUser | null,
  urlPath?: string,
  considerPermissions: boolean = true,
): boolean {
  if (hasMobileAppSubscription(user)) return true
  if (userHasFamilyLicense(user)) return true

  // Family admin without a license should not have full platform access
  if (user?.familyInfo?.isFamilyAdmin && !user?.familyInfo?.hasLicense) {
    return false
  }

  const isValidSubscription = hasValidSubscription(user)

  if (isValidSubscription) {
    if (considerPermissions) {
      return hasPermissionForPath(user, urlPath)
    }
    return isValidSubscription
  }

  return false
}

export function userHasSubscription(user?: IUser | null): boolean {
  if (hasMobileAppSubscription(user)) return true
  if (userHasFamilyLicense(user)) return true

  return hasValidSubscription(user)
}

export function userHasCustomerPortal(user?: IUser | null): boolean {
  // No Stripe customer portal in the school platform — covers the settings tab and the menu entry.
  if (appConfig.isSchoolPlatform) return false

  return Boolean(user?.stripeSubscription?.[0]?.subscriptionStatus || user?.isSubscriptionApplicable)
}

const userHasFamilyLicense = (user?: IUser | null): boolean => {
  const hasFamilyLicense = user?.familyInfo?.hasLicense

  if (hasFamilyLicense) return true

  return false
}

function hasPermissionForPath(user?: IUser | null, urlPath?: string): boolean {
  const permissions = user?.stripeSubscription?.[0]?.permissions
  if (!permissions?.length) return true

  // `next/router`'s singleton default import throws "No router instance found" when any property is
  // accessed during SSR (it's only populated on the client). Avoid touching it on the server; if
  // `urlPath` is provided we can still evaluate permissions against that path.
  if (!urlPath && (typeof window === 'undefined' || !router.isReady)) return true

  const currentPath = urlPath || router.asPath
  return permissions.some((permission) => {
    if (permission.endsWith('/*')) {
      return currentPath?.startsWith(permission.slice(0, permission.length - 2))
    } else {
      return currentPath === permission
    }
  })
}

function hasValidSubscription(user?: IUser | null): boolean {
  const status = user?.stripeSubscription?.[0]?.subscriptionStatus

  return (
    status === StripeSubscriptionStatus.ACTIVE ||
    status === StripeSubscriptionStatus.TRIALING ||
    status === StripeSubscriptionStatus.PAUSED ||
    status === StripeSubscriptionStatus.PAST_DUE
  )
}

function computeHasPremiumAccess(user: IUser | null | undefined, authenticated: boolean, path?: string): boolean {
  if (!authenticated) return false
  if (!user) return true
  const isSubscriptionRequired = isSubscriptionRequiredForUser(user, path)
  return !isSubscriptionRequired || Boolean(user.hasFreemiumTrial)
}

function mergeUserFromMeResponse(meData: IUser): IUser {
  const subscriptionData = getSubScriptionFromUser(meData)
  return {
    ...meData,
    ...subscriptionData,
    curriculumCountryCode: meData.curriculumCountryCode?.toLowerCase(),
  }
}

export const useHasPremiumAccess = (): boolean => {
  // Read `asPath` from the SSR-safe `useRouter()` hook rather than the `next/router` singleton,
  // which throws "No router instance found" during server rendering.
  const { isReady: routerIsReady, asPath } = useRouter()
  const path = routerIsReady ? asPath : undefined

  const user = useCurrentUser()
  const authenticated = useAppSelector(isAuthenticated)
  const {
    data: meData,
    isError,
    isFetching,
    isLoading,
    isSuccess,
  } = useGetMeQuery(undefined, {
    skip: !authenticated,
  })

  const meMerged = useMemo((): IUser | null => {
    if (!isSuccess || !meData) return null
    return mergeUserFromMeResponse(meData)
  }, [isSuccess, meData])

  const meProfileUnsettled = authenticated && (isLoading || isFetching || (!isSuccess && !isError))

  const fromStore = computeHasPremiumAccess(user, authenticated, path)
  const fromMe = meMerged ? computeHasPremiumAccess(meMerged, authenticated, path) : false

  return fromStore || fromMe || meProfileUnsettled
}

export const useHasUserAvailedFreemium = (): boolean => {
  const user = useCurrentUser()
  return Boolean(user?.hasFreemiumAvailed)
}
